import { useMemo } from 'react';

import type { MenuLayout } from '@/shared/api/types';
import { useCurrentUserOrNull } from '@/shared/auth/hooks';
import { useModuleRegistry } from '@/shared/registry/context';
import { arrangeMenu, isEmptyLayout, type ArrangedMenu } from '@/shared/registry/menuLayout';
import { isNavItemVisible } from '@/shared/registry/navVisibility';
import type { NavItem } from '@/shared/registry/types';

import { usePortalSettings } from '../brand/portal';

/** The menu items the signed-in person may open (before an item's own `useVisible` hook), in their natural order. */
export function useNavItems(): NavItem[] {
  const registry = useModuleRegistry();
  const me = useCurrentUserOrNull();
  return useMemo(() => registry.navItems.filter((item) => isNavItemVisible(item, me)), [registry, me]);
}

export interface MenuState {
  /** The arrangement in force: the person's own, else the portal's default, else none (the natural order). */
  layout: MenuLayout | null;
  /** The person's own arrangement; null while they have none (the default applies). */
  own: MenuLayout | null;
  /** The administrators' default arrangement; null while none was set. */
  portalDefault: MenuLayout | null;
}

/** Which arrangement of the left menu applies: the person's own wins over the portal's default until they reset it. */
export function useMenuState(): MenuState {
  const me = useCurrentUserOrNull();
  const portal = usePortalSettings();
  const own = isEmptyLayout(me?.menu) ? null : (me?.menu ?? null);
  const portalDefault = isEmptyLayout(portal.data?.menu) ? null : (portal.data?.menu ?? null);
  return { layout: own ?? portalDefault, own, portalDefault };
}

/** The items the menu shows and the ones it hides, in the arrangement in force. */
export function useArrangedMenu(): ArrangedMenu<NavItem> {
  const items = useNavItems();
  const { layout } = useMenuState();
  return useMemo(() => arrangeMenu(items, layout), [items, layout]);
}
